import React, { useMemo } from "react";

import { Box, Tooltip } from "@mui/material";
import { CalendarProps } from "Components/DiaryCalendar";
import { VipChip } from "Components/Generic/VipChip";
import { DespatchTask } from "Models/despatch.task";
import { Event } from "react-big-calendar";
import { useTranslation } from "react-i18next";
import { capitalize } from "Util/capitalize";
import { useAppState } from "Context/StateContext";
import { getCompleteDateRangeLabel } from "Util/startDate";
import { differenceInDays, startOfDay, startOfToday } from "date-fns";

import { getOrderCustomerDisplayName } from "../../../../../../util/names";
import { TaskBadgeProps } from "../types";
import {
  WeekCard,
  WeekCardBadge,
  WeekCardInfoLine,
  WeekCardTimeLabel,
  WeekCardTitle,
} from "./styled";

interface BadgeProps {
  event: Event;
  style?: React.CSSProperties & { xOffset: number };
  onSelectEvent?: (event: Event, reactEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

const ONE_HOUR_PERCENT = 100 / 24;

const Badge = (props: BadgeProps) => {
  const { event, style, onSelectEvent } = props;
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const isVip = !!event.resource.task.job.vipTreatment;
  const isAssigned = useMemo(() => {
    const task = event.resource.task as DespatchTask;
    const team =
      task.team ??
      task.teams?.find((t) => {
        const createdTo = new Date(t.createdTo!);
        return differenceInDays(startOfToday(), startOfDay(createdTo)) === 0;
      });

    return !!(team?.memberLeader && team?.members?.length);
  }, [event]);

  const isSmall = Number(style?.height) <= ONE_HOUR_PERCENT;

  return (
    <WeekCard
      data-cy="taskBadgeWeek"
      onClick={(htmlEvent) => onSelectEvent?.(event, htmlEvent)}
      topPercent={Number(style?.top)}
      heightPercent={Number(style?.height)}
      leftPercent={Number(style?.xOffset)}
      isVip={isVip}
      isAssigned={isAssigned}
      isSmall={isSmall}
    >
      <Box display="flex" width="100%">
        <WeekCardTitle isVip={isVip} isAssigned={isAssigned}>
          {translate(event.resource.task.type)}
        </WeekCardTitle>

        {isVip && (
          <VipChip
            vipTreatment={event.resource.task.job.vipTreatment}
            sx={{ marginLeft: "auto" }}
          />
        )}
      </Box>

      {!isSmall && <WeekCardInfoLine>#{event.resource?.order?.reference}</WeekCardInfoLine>}

      {!isSmall && (
        <WeekCardInfoLine>{getOrderCustomerDisplayName(event.resource?.order)}</WeekCardInfoLine>
      )}

      {event.start && (
        <WeekCardTimeLabel>
          {getCompleteDateRangeLabel({
            startDate: event.start,
            endDate: event.end,
            separator: "-",
            preferences: currentUser?.preferences,
          })}
        </WeekCardTimeLabel>
      )}
    </WeekCard>
  );
};

interface GroupBadgeProps {
  event: Event;
  style?: React.CSSProperties & { xOffset: number };
  onShowMoreEvents?: CalendarProps["onShowMoreEvents"];
}

const GroupBadge = (props: GroupBadgeProps) => {
  const { event, style, onShowMoreEvents } = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const task = event.resource.tasks.at(0);

  const counter = useMemo(
    () =>
      ((event.resource?.tasks as Event[]) ?? []).reduce(
        (previousValue, currentValue) => {
          const task = currentValue.resource?.task as DespatchTask;

          const team = task.teams?.find((t) => {
            const createdTo = new Date(t.createdTo!);
            return differenceInDays(startOfToday(), startOfDay(createdTo)) === 0;
          });

          if (team?.members?.length && team?.memberLeader) {
            previousValue.alreadyAssigned += 1;
          } else {
            previousValue.toAssign += 1;
          }

          if (task.job.vipTreatment) {
            previousValue.vips += 1;
          }

          return previousValue;
        },
        { alreadyAssigned: 0, vips: 0, toAssign: 0 }
      ),
    [event.resource?.tasks]
  );

  return (
    <WeekCard
      data-cy="taskBadgeWeekGroup"
      topPercent={Number(style?.top)}
      heightPercent={Number(style?.height)}
      leftPercent={Number(style?.xOffset)}
      onClick={() => onShowMoreEvents?.(event.resource.tasks, event.start || new Date())}
      isGroup
    >
      <Box display="flex" width="100%">
        <WeekCardTitle>
          {`${event.resource.tasks.length > 99 ? "+99" : event.resource.tasks.length} ${capitalize(
            translate("tasks")
          )}`}
        </WeekCardTitle>

        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="flex-end"
          gap="4px"
          sx={{ marginLeft: "auto" }}
        >
          <Tooltip title={translate("tasksWithNoAssign")}>
            <WeekCardBadge>{counter.toAssign}</WeekCardBadge>
          </Tooltip>

          <Tooltip title={translate("normalTasksWithAssign")}>
            <WeekCardBadge isAssigned>{counter.alreadyAssigned}</WeekCardBadge>
          </Tooltip>

          <Tooltip title={translate("vipTasksWithAssign")}>
            <WeekCardBadge isVip>{counter.vips}</WeekCardBadge>
          </Tooltip>
        </Box>
      </Box>

      {task && (
        <WeekCardTimeLabel>
          {getCompleteDateRangeLabel({
            startDate: task.start,
            endDate: task.end,
            separator: "-",
            preferences: currentUser?.preferences,
          })}
        </WeekCardTimeLabel>
      )}
    </WeekCard>
  );
};

export const TaskBadgeWeek = (props: TaskBadgeProps) => {
  const { event, style, onShowMoreEvents, onSelectEvent } = props;

  const singleEvent = event?.resource?.tasks.at(0);

  if (!singleEvent) return null;

  if (event.resource.group) {
    return <GroupBadge event={event} onShowMoreEvents={onShowMoreEvents} style={style} />;
  }

  return <Badge event={singleEvent} style={style} onSelectEvent={onSelectEvent} />;
};
